import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import type React from 'react';
import { useMemo, useState } from 'react';
import type { BranchOfficeData } from 'services/types/survey';
import { NAVY } from 'styles/tokens';

interface SurveysBranchMatrixProps {
  data: BranchOfficeData[] | undefined;
  isLoading: boolean;
}

const columns: GridColDef[] = [
  { field: 'branchOffice', headerName: 'Sucursal', width: 180 },
  { field: 'loaded', headerName: 'Cargadas', width: 110 },
  { field: 'triggered', headerName: 'Enviadas', width: 110 },
  { field: 'finished', headerName: 'Respondidas', width: 120 },
  { field: 'pending', headerName: 'Pendientes', width: 110 },
  { field: 'aborted', headerName: 'Rechazadas', width: 120 },
  {
    field: 'npsAverage',
    headerName: 'NPS Promedio',
    width: 130,
    valueFormatter: (value: number | null) => (value !== null ? value.toFixed(1) : '-'),
  },
  { field: 'npsResponses', headerName: 'Respuestas NPS', width: 140 },
];

interface BranchRow extends BranchOfficeData {
  id: number;
}

const SurveysBranchMatrix: React.FC<SurveysBranchMatrixProps> = ({ data, isLoading }) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const rows: BranchRow[] = useMemo(() => {
    if (!data) return [];
    return data.map((row, index) => ({ id: index, ...row }));
  }, [data]);

  const pagedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return rows.slice(start, start + paginationModel.pageSize);
  }, [rows, paginationModel]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', color: `${NAVY}80` }}>
          No hay datos por sucursal disponibles
        </Typography>
      </Box>
    );
  }

  return (
    <CustomDataGrid<BranchRow>
      rows={pagedRows}
      columns={columns}
      filterModel={filterModel}
      sortModel={sortModel}
      onFilterModelChange={setFilterModel}
      onSortModelChange={setSortModel}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      rowCount={rows.length}
      getRowClassName={() => ''}
      pageId="surveys-branch-matrix"
      disableFilters
    />
  );
};

export default SurveysBranchMatrix;
